import {
  Badge,
  Checkbox,
  DescriptionList,
  SettingsSection,
  Stack,
  Text,
  useToast,
  type DescriptionListItem,
} from '@hkdevs/design-system';
import { useId, useState } from 'react';
import {
  useSavePayHistory,
  type AdminSettings,
  type PayHistorySettings,
} from '../../api/queries/settings';
import { withReference } from '../../api/withReference';

/** What each part is, and why the breakdown needs the summary. */
export const ABOUT = {
  summary: 'The scope selector and its dates, and the Earnings, Deductions and Total boxes.',
  breakdown: 'Earnings and deductions by category, for the summary’s scope.',
  needsSummary: 'Shows only with the pay summary, since it uses the summary’s scope.',
};

const onOff = (on: boolean) =>
  on ? (
    <Badge tone="success" dot>
      On
    </Badge>
  ) : (
    <Badge tone="neutral">Off</Badge>
  );

/**
 * The pay history page's parts (A15), for everyone who sees the page: the pay summary and
 * the category breakdown. They're saved apart; while the summary is off the breakdown's
 * box is disabled and keeps its saved value, since the breakdown takes the summary's scope.
 */
export function PayHistorySection({ settings }: { settings: AdminSettings }) {
  const id = useId();
  const save = useSavePayHistory();
  const { toast } = useToast();
  const saved: PayHistorySettings = {
    showPaySummary: settings.showPaySummary,
    showCategoryBreakdown: settings.showCategoryBreakdown,
  };
  const [draft, setDraft] = useState(saved);
  const dirty =
    draft.showPaySummary !== saved.showPaySummary ||
    draft.showCategoryBreakdown !== saved.showCategoryBreakdown;

  const items: DescriptionListItem[] = [
    {
      term: 'Pay summary',
      description: (
        <Stack gap={1}>
          {onOff(saved.showPaySummary)}
          <Text size="meta" tone="muted">
            {ABOUT.summary}
          </Text>
        </Stack>
      ),
    },
    {
      term: 'Category breakdown',
      description: (
        <Stack gap={1}>
          {onOff(saved.showCategoryBreakdown)}
          <Text size="meta" tone="muted">
            {saved.showPaySummary ? ABOUT.breakdown : ABOUT.needsSummary}
          </Text>
        </Stack>
      ),
    },
  ];

  return (
    <SettingsSection
      title="Pay history page"
      description="Choose what reps, supervisors and pay viewers see above their checks. The checks always show."
      mode="form"
      editLabel="Edit pay history page"
      onEditingChange={(editing) => {
        if (editing) setDraft(saved);
      }}
      dirty={dirty}
      onSave={() =>
        save.mutateAsync(draft).then(
          () => {
            toast({ tone: 'success', title: 'Pay history page saved' });
          },
          (error: unknown) => {
            toast(
              withReference({ tone: 'danger', title: 'Pay history page couldn’t be saved' }, error),
            );
            throw new Error('Check your connection and try again.');
          },
        )
      }
      form={
        <Stack gap={3}>
          <Stack gap={1}>
            <Checkbox
              label="Pay summary"
              checked={draft.showPaySummary}
              aria-describedby={`${id}summary`}
              onChange={(event) => setDraft({ ...draft, showPaySummary: event.target.checked })}
            />
            <Text id={`${id}summary`} size="meta" tone="muted">
              {ABOUT.summary}
            </Text>
          </Stack>
          <Stack gap={1}>
            <Checkbox
              label="Category breakdown"
              checked={draft.showCategoryBreakdown}
              disabled={!draft.showPaySummary}
              aria-describedby={`${id}breakdown`}
              onChange={(event) =>
                setDraft({ ...draft, showCategoryBreakdown: event.target.checked })
              }
            />
            <Text id={`${id}breakdown`} size="meta" tone="muted">
              {draft.showPaySummary ? ABOUT.breakdown : ABOUT.needsSummary}
            </Text>
          </Stack>
        </Stack>
      }
    >
      <DescriptionList items={items} />
    </SettingsSection>
  );
}
